import React, { useRef, useState, useEffect } from 'react';
import { Play, Pause, Music2 } from 'lucide-react';

const SONG_URL = '/justdeliver-song.mp3';

const fmt = (s) => {
  if (!s || !isFinite(s)) return '0:00';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec < 10 ? '0' : ''}${sec}`;
};

export const SongPlayer = ({ testId = 'song-player' }) => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onTime = () => setCurrent(a.currentTime);
    const onMeta = () => setDuration(a.duration);
    const onEnd = () => { setPlaying(false); setCurrent(0); };
    a.addEventListener('timeupdate', onTime);
    a.addEventListener('loadedmetadata', onMeta);
    a.addEventListener('ended', onEnd);
    return () => {
      a.removeEventListener('timeupdate', onTime);
      a.removeEventListener('loadedmetadata', onMeta);
      a.removeEventListener('ended', onEnd);
    };
  }, []);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) {
      a.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    } else {
      a.pause();
      setPlaying(false);
    }
  };

  const seek = (e) => {
    const a = audioRef.current;
    if (!a || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = Math.min(Math.max((e.clientX - rect.left) / rect.width, 0), 1);
    a.currentTime = pct * duration;
    setCurrent(a.currentTime);
  };

  const pct = duration ? (current / duration) * 100 : 0;

  return (
    <div
      className="mb-6 rounded-2xl border border-[#2E7D32]/20 bg-gradient-to-r from-[#F1F8F2] to-white p-3 shadow-sm"
      data-testid={testId}
    >
      <p className="text-sm font-semibold text-[#1B5E20] mb-2 leading-snug">
        🎶 Listen to our JustDeliver.ie song, you won't get it out of your head! 😂🎶
      </p>
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={toggle}
          aria-label={playing ? 'Pause song' : 'Play song'}
          className="shrink-0 w-11 h-11 rounded-full bg-[#2E7D32] text-white flex items-center justify-center shadow-md hover:bg-[#256628] active:scale-95 transition-transform"
          data-testid={`${testId}-toggle`}
        >
          {playing ? <Pause className="w-5 h-5" fill="currentColor" /> : <Play className="w-5 h-5 ml-0.5" fill="currentColor" />}
        </button>

        <div className="flex-1 min-w-0">
          <div
            className="h-2 rounded-full bg-[#2E7D32]/15 cursor-pointer overflow-hidden"
            onClick={seek}
            data-testid={`${testId}-progress`}
          >
            <div
              className="h-full rounded-full bg-[#F57C00] transition-[width] duration-150"
              style={{ width: `${pct}%` }}
            />
          </div>
          <div className="flex items-center justify-between mt-1">
            <span className="text-xs text-gray-500 tabular-nums" data-testid={`${testId}-time`}>
              {fmt(current)} / {fmt(duration)}
            </span>
            <span className="flex items-center gap-1 text-xs text-[#2E7D32] font-medium">
              <Music2 className="w-3 h-3" /> Our song
            </span>
          </div>
        </div>
      </div>
      <audio ref={audioRef} src={SONG_URL} preload="metadata" data-testid={`${testId}-audio`} />
    </div>
  );
};

export default SongPlayer;
